import React, { useCallback, useRef, useState } from 'react';
import { useLoaderData, useNavigate, useSearchParams } from '@remix-run/react';
import { useLocalStorageState } from 'ahooks';
import FileIcon from './FileIcon.png';
import FileIconCorner from './FileIconCorner.png';
import { Caption, useStyles } from '@archipro-design/aria';
import * as S from './Downloads.style';
import { isValidURL } from '@modules/root';
import type LibraryDownload from '@modules/product/type/library-download';
import {
    DOWNLOAD_ATTR_FILE_ID,
    DOWNLOAD_ATTR_FILE_TITLE,
    DOWNLOAD_ATTR_PRODUCT_ID,
    DOWNLOAD_ATTR_PROFESSIONAL_ID,
    DOWNLOAD_ATTR_PROFESSIONAL_TITLE,
    TRACK_DOWNLOAD_ATTR,
    useTracker,
} from '@archipro-website/tracker';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import DownloadConfirmationModal from './DownloadConfirmationModal';
import { useUser, isUserLoggedIn } from '~/modules/user';
import { useABTestIsOn } from '~/modules/root/hook/use-growthbook';
import whitelistedProfessionalIDs from '~/modules/root/growthbook-whitelist/logged-in-share-information-downloads';

interface DownloadProps {
    data: LibraryDownload;
    professional?: string;
}

const DONT_ASK_AGAIN_SESSION_KEY = 'dontAskAgainDownloadContactDetailSharing';

const Download = ({ data }: DownloadProps) => {
    const user = useUser();
    const isLoggedIn = isUserLoggedIn(user);
    const { css } = useStyles();
    const { product } = useLoaderData<ProductPageLoaderData>();
    const tracking = useTracker();
    const [modalOpen, setModalOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const hasBeenTracked = useRef(false);
    const [dontAskAgain, setDontAskAgain] = useLocalStorageState<boolean>(
        DONT_ASK_AGAIN_SESSION_KEY
    );
    const showShareDetailsDownload = useABTestIsOn(
        'logged-in-share-information-downloads',
        product.Professional.ID,
        whitelistedProfessionalIDs
    );

    const trackDownload = useCallback(
        (fromModal: boolean = false) => {
            if (hasBeenTracked.current) {
                return;
            }

            // track the current page with download tab open
            const url = new URL(window.location.href);
            if (!url.searchParams.has('openDownloadModal')) {
                url.searchParams.append('openDownloadModal', '1');
            }

            if (fromModal) {
                tracking.log('DownloadsContent', {
                    url: url,
                    label: product.Professional.Title,
                    data: {
                        ExtraData: data.Title,
                        professionalID: product.Professional.ID,
                        productID: product.ID,
                        fileTitle: data.Title,
                        fileID: data.ID,
                    },
                    targetTracker: ['archiproTracker', 'ga4Tracker'],
                });
            }

            tracking.log('DownloadsClick', {
                url: url,
                label: product.Professional.Title,
                data: {
                    ExtraData: data.Title,
                    professionalID: product.Professional.ID,
                    productID: product.ID,
                    fileTitle: data.Title,
                    fileID: data.ID,
                },
            });
            hasBeenTracked.current = true;
        },
        [
            tracking,
            product.Professional.Title,
            product.Professional.ID,
            product.ID,
            data.Title,
            data.ID,
        ]
    );

    const downloadContent = useCallback(
        (downloadLink: string, fromModal: boolean = false) => {
            window.open(downloadLink, '_blank', 'noopener');
            trackDownload(fromModal);
            setModalOpen(false);
        },
        [trackDownload]
    );

    React.useEffect(() => {
        if (searchParams.get('openDownloadModal') === data.ID.toString()) {
            searchParams.delete('openDownloadModal');
            setSearchParams(searchParams);
            if (dontAskAgain) {
                const downloadLink = data.ExternalLink
                    ? data.ExternalLink
                    : data.InternalLink;
                if (!downloadLink) return;
                downloadContent(downloadLink);
            } else {
                setModalOpen(true);
            }
        }
    }, [searchParams, data, dontAskAgain, downloadContent, setSearchParams]);

    const link = data.ExternalLink ? data.ExternalLink : data.InternalLink;
    if (!link) {
        return null;
    }

    const toggleModal = () => {
        setModalOpen((modalOpen) => !modalOpen);
        if (searchParams.get('openDownloadModal')) {
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.delete('openDownloadModal');
            const paramString = urlParams.toString();
            navigate(
                window.location.pathname + paramString ? '' : `?${paramString}`
            );
        }
    };

    const updateDontAskAgain = (value: boolean) => {
        setDontAskAgain(value);
    };

    const extension = isValidURL(link) ? 'url' : fileExtensionFromFile(link);

    const downloadBody = () => {
        return (
            <>
                <div className={css(S.DownloadExtension)}>{extension}</div>
                <img
                    src={FileIconCorner}
                    className={css(S.DownloadTileCorner)}
                    alt=""
                />
                <img
                    src={FileIcon}
                    className={css(S.DownloadTileImage)}
                    alt=""
                />
                <Caption
                    variant="04"
                    maximumLines={2}
                    className={css(S.DownloadTileLabel)}
                >
                    {data.Title}
                </Caption>
            </>
        );
    };

    return (
        <>
            {!showShareDetailsDownload ? (
                <a
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    className={css(S.DownloadContainer)}
                    {...{
                        [TRACK_DOWNLOAD_ATTR]: '1',
                        [DOWNLOAD_ATTR_PROFESSIONAL_ID]:
                            product.Professional.ID,
                        [DOWNLOAD_ATTR_PROFESSIONAL_TITLE]:
                            product.Professional.Title,
                        [DOWNLOAD_ATTR_FILE_ID]: data.ID,
                        [DOWNLOAD_ATTR_FILE_TITLE]: data.Title,
                        [DOWNLOAD_ATTR_PRODUCT_ID]: product.ID,
                    }}
                >
                    {downloadBody()}
                </a>
            ) : (
                <>
                    <div
                        onClick={() => {
                            // use window.localStorage.getItem to check the value across tabs
                            if (
                                isLoggedIn &&
                                window.localStorage.getItem(
                                    DONT_ASK_AGAIN_SESSION_KEY
                                ) !== 'true'
                            ) {
                                return setModalOpen(true);
                            }
                            downloadContent(link, false);
                        }}
                        className={css(S.DownloadContainer)}
                    >
                        {downloadBody()}
                    </div>
                    <DownloadConfirmationModal
                        modalOpen={modalOpen}
                        toggleModal={toggleModal}
                        onDownload={() => downloadContent(link, true)}
                        dontAskAgainChecked={dontAskAgain}
                        updateDontAskAgain={updateDontAskAgain}
                    />
                </>
            )}
        </>
    );
};

const fileExtensionFromFile = (fileName: string): string => {
    const endOfString = fileName.split('.').pop();
    if (!endOfString) {
        return '';
    }

    // Check for extra characters after the extension urls
    // can sometimes have query params
    const cleaned = endOfString.substring(0, endOfString.indexOf('?'));

    return cleaned ? cleaned : endOfString;
};

export default Download;
